import React, {
  ChangeEvent,
  FC,
  FormEvent,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useDispatch } from "react-redux";
import {
  Grid,
  TextField,
  Typography,
  Button,
  FormControl,
  InputLabel,
  Select,
  Menu,
  MenuItem,
  IconButton,
  Box,
  Link as MuiLink,
  InputAdornment,
  FormHelperText,
  SelectChangeEvent,
  Checkbox,
  Card,
  Divider,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import { OpenInNew } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery } from "@apollo/client";

import BusinessAddressForm, { BusinessAddress } from "./businessAddressForm";
import { UserContext } from "../../auth/auth-context";
import { Permissions, hasPermission } from "../../../util/accessControl";
import { rootClientPath } from "../../../util/link";
import {
  POST_BUSINESS_BASIC,
  UPDATE_BUSINESS_BASIC,
  SEND_EMAIL_MUTATION,
} from "../../../graphql/mutations";
import { GET_ALL_COUNTRIES } from "../../../graphql/queries/countryStateCity";
import {
  BusinessQueryNames,
  GET_BUSINESS_CATEGORIES,
} from "../../../graphql/queries";
import { BusinessBasic, BusinessCategory } from "../../../types";
import { useSelectedBusiness } from "../../../redux/store";
import {
  addBusinesses,
  setSelectedBusiness,
  updateBusiness,
} from "../../../redux/actions/businessActions";
import useLoadReduxStoreData, {
  normalizeBusinessData,
} from "../../../redux/reducers/loadReduxStoreData";
import { BusinessStatus, getEffectivePlan } from "../../../util/utils";
import UpgradePlan from "../../../util/upgrade/upgradePlan";
import GenericTooltip from "../../common/genericToolTip";
import ConfirmModal from "./businessSubmitConfirmation";
import { useNavigate } from "react-router-dom";
import { useAuthStatus } from "../../../util/useAuthStatus";

interface IBusinessFormProps {
  isDisabled?: boolean;
  onSubmitSuccess?: () => void;
  isNewBusiness?: boolean;
}

type DynamicFieldKey = "emails" | "links";

const socialMediaOptions = [
  "facebook",
  "twitter",
  "instagram",
  "linkedin",
  "other",
];
const tooltipData = {
  url: "This is your Plany365 business profile URL for sharing with others. Changing this URL will break any previously shared links—including staff links—so you'll need to update those to keep them working.",
  displayOnBookingPage:
    "Display this business on the Plany365 Booking Page for public visibility. (This will be enabled only if the business has already been created.)",
  businessProfileName:
    "If you have a registered business, use your official business name. Otherwise, enter your full legal name.",
};

const standardPlanRequired = 2;

const BusinessForm: FC<IBusinessFormProps> = ({
  isDisabled: disabledProp = false,
  isNewBusiness,
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const userContext = useContext(UserContext);
  const providerUser = userContext.providerUser!;
  const selectedBusiness = useSelectedBusiness();
  const businessBasic = selectedBusiness?.businessBasic;
  const [showConfirmation, setShowConfirmation] = useState(false);

  // useEffect(() => {
  //   console.log("🔁 BusinessForm MOUNTED:", { shouldInitNew, isNewBusiness });
  //   return () => console.log("🧹 BusinessForm UNMOUNTED");
  // }, []);

  // --- GraphQL ---
  const { loading: loadingCountries, data: countryDataRaw } =
    useQuery(GET_ALL_COUNTRIES);
  const { loading: loadingCats, data: categoryDataRaw } = useQuery(
    GET_BUSINESS_CATEGORIES
  );
  const countries = countryDataRaw?.getAllCountries || [];
  const categories: BusinessCategory[] =
    categoryDataRaw?.getBusinessCategories || [];
  const { isLoggedIn } = useAuthStatus();
  const { refetch: reloadBusinesses } = useLoadReduxStoreData(
    BusinessQueryNames.GET_MY_BUSINESSES,
    { enabled: isLoggedIn }
  );
  const [postBusiness] = useMutation(POST_BUSINESS_BASIC);
  const [updateBusinessM] = useMutation(UPDATE_BUSINESS_BASIC);
  const [sendEmail] = useMutation(SEND_EMAIL_MUTATION);

  const shouldInitNew = 
    isNewBusiness ||
    (!!providerUser?.userId &&
      Number(providerUser?.defaultBusinessId || 0) <= 0);

  // --- Helpers ---
  const getInitialForm = (): BusinessBasic => ({
    id: 0,
    businessCategoryId: null,
    name: "",
    description: "",
    currency: "USD",
    status: BusinessStatus.ACTIVE,
    street: "",
    address: "",
    countryId: -1,
    stateId: -1,
    cityId: -1,
    zipcode: "",
    lat: 0,
    lng: 0,
    hasLocation: true,
    url: "",
    emails: [""],
    phoneNumbers: [{ countryCode: "", phoneNumber: "" }],
    links: [],
    images: [],
    createdBy: providerUser.userId,
    stage: 0,
    displayOnBookingPage: false,
  });

  // --- State Declarations ---
  const [form, setForm] = useState<BusinessBasic>(
    shouldInitNew ? getInitialForm() : businessBasic || getInitialForm()
  );

  const [address, setAddress] = useState<BusinessAddress | undefined>(
    !shouldInitNew && businessBasic
      ? {
          street: businessBasic.street,
          countryId: Number(businessBasic.countryId),
          stateId: businessBasic.stateId,
          cityId: businessBasic.cityId,
          zipcode: businessBasic.zipcode,
        }
      : undefined
  );

  const [touched, setTouched] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [checkPlanNeeded, setCheckPlanNeeded] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [socialList, setSocialList] = useState<string[]>(socialMediaOptions);
  const [isDirty, setIsDirty] = useState(false);
  const navigate = useNavigate();

  // Determine if the user should start with an empty form

  // --------------------------------------------------------------------
  // Combined New vs. Edit initializer
  // --------------------------------------------------------------------
  useEffect(() => {
    if (shouldInitNew) {
      setForm(getInitialForm());
      setAddress(undefined);
      setErrors({});
      setTouched(false);
      setIsDirty(false);
      setSocialList(socialMediaOptions);
      setAnchorEl(null);
    } else if (businessBasic) {
      setForm(businessBasic);
      setAddress({
        street: businessBasic.street,
        countryId: businessBasic.countryId,
        stateId: businessBasic.stateId,
        cityId: businessBasic.cityId,
        zipcode: businessBasic.zipcode,
      });
      setErrors({});
      setTouched(false);
      setIsDirty(false);
      const used = (businessBasic.links || []).map((l) => l.platform);
      setSocialList(socialMediaOptions.filter((plat) => !used.includes(plat)));
      setAnchorEl(null);
    }
  }, [shouldInitNew, businessBasic]);

  // --------------------------------------------------------------------

  // --- Permission logic ---
  const businessStaffs = selectedBusiness?.businessStaffs || [];
  const myStaff = businessStaffs.find((s) => s.id === providerUser.staffId);
  const canEdit =
    shouldInitNew ||
    hasPermission(
      myStaff?.role?.permissions || [],
      Permissions.Business.EDIT
    ) ||
    businessBasic?.createdBy === providerUser.userId;

  const isFormDisabled = (!shouldInitNew && Boolean(disabledProp)) || !canEdit;

  useEffect(() => {
    if (!selectedBusiness && providerUser?.defaultBusinessId) {
      reloadBusinesses().then((result) => {
        const businesses = result?.data?.myBusinesses || [];
        const matched = businesses.find(
          (b: any) => Number(b.id) === Number(providerUser.defaultBusinessId)
        );
        if (matched) {
          dispatch(setSelectedBusiness(Number(matched.id)));
        }
      });
    }
  }, [
    selectedBusiness,
    providerUser?.defaultBusinessId,
    reloadBusinesses,
    dispatch,
  ]);

  // --- Handlers (input, dynamic fields, etc.) ---
  const handleInputChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
    setTouched(true);
    setIsDirty(true);
  };
  const handleSelectChange = (e: SelectChangeEvent<string>) => {
    const { name, value } = e.target as { name: string; value: any };
    setForm((f) => ({ ...f, [name]: value }));
    setTouched(true);
    setIsDirty(true);
  };
  // Dedicated handler for checkboxes
  const handleCheckBoxStateChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const effectivePlan = getEffectivePlan(providerUser);
    if (effectivePlan < standardPlanRequired) {
      setCheckPlanNeeded(true);
      return;
    }
    const { name, checked } = event.target;
    setForm((prev) => ({
      ...prev,
      [name]: checked,
    }));
    setIsDirty(true);
  };
  const MAX_PHONE_LENGTH = 10;
  const handleDynamicFieldChange = (
    key: DynamicFieldKey,
    idx: number,
    value: any
  ) => {
    setForm((f) => {
      const arr = [...(f[key] || [])];
      arr[idx] = value;
      return { ...f, [key]: arr };
    });
    setTouched(true);
    setIsDirty(true);
  };
  const handlePhoneAreaCodeChange = (idx: number, value: string) => {
    setForm((f) => {
      const arr = [...(f.phoneNumbers || [])];
      arr[idx] = { ...arr[idx], countryCode: value };
      return { ...f, phoneNumbers: arr };
    });
    setTouched(true);
    setIsDirty(true);
  };

  const handleUrlChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    // Allow letters, numbers, dashes (-), underscores (_), and dots (.)
    const urlRegex = /^[a-zA-Z0-9-_\.]*$/;

    if (!urlRegex.test(value)) {
      console.error(
        "Invalid URL: only letters, numbers, hyphens (-), underscores (_), and dots (.) are allowed."
      );
      return;
    }

    // Update the URL field in the form
    setForm((f) => ({
      ...f,
      url: value,
    }));

    setTouched(true);
    setIsDirty(true);
  };

  const handlePhoneNumberChange = (idx: number, value: string) => {
    // Validation: only digits allowed
    if (!/^\d*$/.test(value)) {
      console.error("Invalid phone number: please enter numbers only.");
      return;
    }

    // Validation: max length
    if (value.length > MAX_PHONE_LENGTH) {
      console.warn(`Phone number cannot exceed ${MAX_PHONE_LENGTH} digits.`);
      return;
    }

    // If valid, update form
    setForm((f) => {
      const arr = [...(f.phoneNumbers || [])];
      arr[idx] = { ...arr[idx], phoneNumber: value };
      return { ...f, phoneNumbers: arr };
    });

    setTouched(true);
    setIsDirty(true);
  };
  const handleAddDynamic = (key: "emails" | "phoneNumbers") => {
    if (key === "phoneNumbers") {
      setForm((f) => ({
        ...f,
        phoneNumbers: [
          ...(f.phoneNumbers || []),
          { countryCode: "", phoneNumber: "" },
        ],
      }));
    } else {
      setForm((f) => ({
        ...f,
        emails: [...(f.emails || []), ""],
      }));
    }
    setTouched(true);
    setIsDirty(true);
  };
  const handleDeleteDynamic = (
    key: "emails" | "phoneNumbers" | "links",
    idx: number
  ) => {
    setForm((f) => ({
      ...f,
      [key]: Array.isArray(f[key])
        ? [...(f[key] as any[]).filter((_, i) => i !== idx)]
        : [],
    }));
    setTouched(true);
    setIsDirty(true);
  };
  const handleAddLink = (platform: string) => {
    setForm((f) => ({
      ...f,
      links: [...(f.links || []), { platform, url: "" }],
    }));
    setSocialList((s) => s.filter((x) => x !== platform));
    setAnchorEl(null);
    setTouched(true);
    setIsDirty(true);
  };

  // --- Submit (create/update) ---
  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    try {
      let updatedBasic: BusinessBasic = {
        ...form,
        ...address,
        stateId:
          typeof address?.stateId === "number" && address.stateId !== -1
            ? address.stateId
            : form.stateId,
        cityId:
          typeof address?.cityId === "number" && address.cityId !== -1
            ? address.cityId
            : form.cityId,
        countryId:
          typeof address?.countryId === "number" && address.countryId !== -1
            ? address.countryId
            : form.countryId,
      };

      if (shouldInitNew || !businessBasic) {
        const { id, images, ...createBusinessInput } = updatedBasic;

        const response = await postBusiness({
          variables: {
            input: createBusinessInput,
          },
        });

        if (response.data) {
          const normalizedBusinesses = normalizeBusinessData(
            response.data.createBusiness
          );
          dispatch(addBusinesses(normalizedBusinesses, "MY_BUSINESSES"));
          try {
            await sendEmail({
              variables: {
                to: providerUser.email,
                subject: `Congratulations! Your business "${response.data?.createBusiness?.name}" is now live on Plany365`,
                text: `Hello,\n\nCongratulations! You have successfully created your business "${response.data?.createBusiness?.name}" on Plany365.\nYou can now access your business dashboard and manage your operations.\n\nThank you for choosing Plany365.\n\nBest regards,\nThe Plany365 Team`,
              },
            });
          } catch (error) {
            console.error(
              "Error sending email during business creation:",
              error
            );
          }

          setForm(updatedBasic);
          setShowConfirmation(true);
          setIsDirty(false);
          const redirectPath = localStorage.getItem("redirectAfterBusiness");
          localStorage.removeItem("redirectAfterBusiness");
          redirectPath && navigate(redirectPath, { replace: true });

          return;
        }
      } else {
        const { images, ...updateBusinessInput } = updatedBasic;

        await updateBusinessM({
          variables: {
            input: updateBusinessInput,
          },
        });
        setForm(updatedBasic);
        dispatch(updateBusiness(updatedBasic.id ?? -1, updatedBasic));
        setIsDirty(false);
      }
    } catch (error) {
      console.error("Unable to create/update business:", error);
    }
  };

  const selectedCountry = countries.find(
    (c: any) => Number(c.id) === Number(form.countryId)
  );
  const computedPhoneCode = useMemo(() => {
    if (!selectedCountry?.phone_code) return "";
    const code = selectedCountry.phone_code.toString();
    return code.startsWith("0") || code.startsWith("+") ? code : `+${code}`;
  }, [selectedCountry]);

  if (loadingCountries || loadingCats) return <p>Loading...</p>;

  return (
    <>
      <ConfirmModal
        open={showConfirmation}
        onClose={() => setShowConfirmation(false)}
      />
      <FormControl component="form" onSubmit={handleSubmit} fullWidth>
        {checkPlanNeeded && (
          <UpgradePlan
            featureName="Display on booking page"
            requiredPlanId={standardPlanRequired}
            onClose={() => setCheckPlanNeeded(false)}
          />
        )}
        <Grid container spacing={3}>
          {/* Business Name */}
          <Grid item xs={12} sm={6}>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <TextField
                fullWidth
                disabled={isFormDisabled}
                required
                label={t("Business Profile Name")}
                name="name"
                value={form.name}
                onChange={handleInputChange}
                error={Boolean(errors.name)}
                helperText={errors.name}
              />
              <Box sx={{ ml: 1 }}>
                <GenericTooltip title={tooltipData.businessProfileName} />
              </Box>
            </Box>

            <Grid>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Checkbox
                  disabled={isFormDisabled || !selectedBusiness}
                  name="displayOnBookingPage"
                  checked={form.displayOnBookingPage}
                  onChange={handleCheckBoxStateChange}
                />
                <span style={{ marginRight: 4 }}>Display on Booking Page</span>
                <GenericTooltip title={tooltipData.displayOnBookingPage} />
              </Box>
            </Grid>
          </Grid>

          {/* URL */}
          <Grid item xs={12} sm={6}>
            <Box display="flex" alignItems="center" gap={1}>
              <Typography color="textSecondary">{rootClientPath}/</Typography>
              <TextField
                fullWidth
                disabled={isFormDisabled}
                required
                label={t("Url")}
                name="url"
                value={form.url}
                onChange={handleUrlChange}
                error={Boolean(errors.url)}
                helperText={errors.url}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <MuiLink
                        href={`${rootClientPath}/${form.url}`}
                        target="_blank"
                        rel="noopener"
                        style={{
                          visibility: form.url ? "visible" : "hidden",
                        }}
                      >
                        <OpenInNew />
                      </MuiLink>
                    </InputAdornment>
                  ),
                }}
              />
              <GenericTooltip title={tooltipData.url} />
            </Box>
          </Grid>

          {/* Category */}
          <Grid item xs={12} sm={6}>
            <FormControl
              fullWidth
              disabled={isFormDisabled}
              error={Boolean(errors.businessCategoryId)}
            >
              <InputLabel>{t("Category")}</InputLabel>
              <Select
                name="businessCategoryId"
                value={
                  form.businessCategoryId !== null
                    ? String(form.businessCategoryId)
                    : ""
                }
                onChange={handleSelectChange}
                required
              >
                <MenuItem value="">
                  <em>{t("Select one")}</em>
                </MenuItem>
                {categories.map((c) => (
                  <MenuItem key={c.id} value={c.id}>
                    {c.name}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>{errors.businessCategoryId}</FormHelperText>
            </FormControl>
          </Grid>

          {/* Description */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              disabled={isFormDisabled}
              label={t("Description")}
              name="description"
              value={form?.description}
              onChange={handleInputChange}
              multiline
              rows={3}
              helperText={`${form.description?.length}/255`}
            />
          </Grid>

          {/* Address */}
          <Grid item xs={12}>
            <Typography variant="h6">{t("Location")}</Typography>
            <BusinessAddressForm
              disabled={isFormDisabled}
              businessAddress={address}
              countries={countries}
              setBusinessAddress={(a) => {
                setAddress(a);
                setTouched(true);
                setIsDirty(true);
              }}
              setActiveCountry={(country) => {
                const rawCode = country.phone_code?.toString() || "";
                const prefixedCode = rawCode.startsWith("+")
                  ? rawCode
                  : `+${rawCode}`;

                setAddress((prev) => ({
                  ...prev,
                  countryId: Number(country.id),
                  stateId: -1,
                  cityId: -1,
                }));

                setForm((prevForm: any) => {
                  const phoneNumbers = [...(prevForm.phoneNumbers || [])];
                  if (phoneNumbers.length === 0) {
                    phoneNumbers.push({
                      countryCode: prefixedCode,
                      phoneNumber: "",
                    });
                  } else {
                    phoneNumbers[0] = {
                      ...phoneNumbers[0],
                      countryCode: prefixedCode,
                    };
                  }
                  return {
                    ...prevForm,
                    countryId: Number(country.id), // ✅ THIS is critical
                    phoneNumbers,
                  };
                });

                setTouched(true);
                setIsDirty(true);
              }}
            />
          </Grid>

          {/* Emails */}
          <Grid item xs={12}>
            <Typography variant="h6">{t("Emails")}</Typography>
            {(form.emails || []).map((em, i) => (
              <Box key={i} display="flex" gap={1} mb={1}>
                <TextField
                  fullWidth
                  disabled={isFormDisabled}
                  label="Email"
                  value={em}
                  onChange={(e) =>
                    handleDynamicFieldChange("emails", i, e.target.value)
                  }
                  error={Boolean(errors[`emails.${i}`])}
                  helperText={errors[`emails.${i}`]}
                />
                <IconButton
                  disabled={isFormDisabled}
                  onClick={() => handleDeleteDynamic("emails", i)}
                >
                  <DeleteIcon />
                </IconButton>
              </Box>
            ))}
            <Button
              startIcon={<AddIcon />}
              disabled={isFormDisabled}
              onClick={() => handleAddDynamic("emails")}
            >
              {t("Add Email")}
            </Button>
          </Grid>

          {/* Phone Numbers */}
          <Grid item xs={12}>
            <Typography variant="h6">{t("Phone Numbers")}</Typography>
            {(form.phoneNumbers || []).map((p, i) => (
              <Box key={i} display="flex" gap={1} mb={1}>
                <TextField
                  label="Country code"
                  value={computedPhoneCode}
                  disabled={true}
                  sx={{ width: "20%" }}
                  onChange={(e) => handlePhoneAreaCodeChange(i, e.target.value)}
                />
                <TextField
                  fullWidth
                  disabled={isFormDisabled}
                  label="Number"
                  value={p.phoneNumber}
                  onChange={(e) => handlePhoneNumberChange(i, e.target.value)}
                />
                {i > 0 && (
                  <IconButton
                    disabled={isFormDisabled}
                    onClick={() => handleDeleteDynamic("phoneNumbers", i)}
                  >
                    <DeleteIcon />
                  </IconButton>
                )}
              </Box>
            ))}
            <Button
              startIcon={<AddIcon />}
              disabled={isFormDisabled}
              onClick={() => handleAddDynamic("phoneNumbers")}
            >
              {t("Add Phone")}
            </Button>
          </Grid>

          {/* Links */}
          <Grid item xs={12}>
            <Typography variant="h6">
              {t("Links")}
              <Button
                startIcon={<AddIcon />}
                disabled={isFormDisabled}
                onClick={(e) => setAnchorEl(e.currentTarget)}
                sx={{ ml: 2 }}
              >
                {t("Add Link")}
              </Button>
            </Typography>
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={() => setAnchorEl(null)}
            >
              {socialList.map((plat) => (
                <MenuItem key={plat} onClick={() => handleAddLink(plat)}>
                  {plat}
                </MenuItem>
              ))}
            </Menu>
            {(form.links || []).map((ln, i) => (
              <Box key={i} display="flex" gap={1} mb={1}>
                <TextField
                  label={ln.platform}
                  value={ln.platform}
                  disabled
                  sx={{ width: "30%" }}
                />
                <TextField
                  fullWidth
                  disabled={isFormDisabled}
                  label="URL"
                  value={ln.url}
                  onChange={(e) =>
                    handleDynamicFieldChange("links", i, {
                      ...ln,
                      url: e.target.value,
                    })
                  }
                />
                <IconButton
                  disabled={isFormDisabled}
                  onClick={() => handleDeleteDynamic("links", i)}
                >
                  <DeleteIcon />
                </IconButton>
              </Box>
            ))}
          </Grid>

          {/* Actions */}
          <Grid item xs={12}>
            <Box display="flex" justifyContent="flex-end" gap={2}>
              <Button
                disabled={isFormDisabled || !isDirty}
                onClick={() => {
                  if (businessBasic) {
                    setForm(businessBasic);
                    setIsDirty(false);
                  }
                }}
              >
                {t("Cancel")}
              </Button>
              <Button
                type="submit"
                variant="contained"
                disabled={isFormDisabled || !isDirty}
              >
                {t("Save")}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </FormControl>
    </>
  );
};

 

export default BusinessForm;
